import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadSimple as UploadIcon, X } from '@phosphor-icons/react';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Props {
  propertyId: string;
  /** Krátký popisek (např. obec nebo GPS) pro header. */
  title?: string;
  onClose: () => void;
  /** Volá se po úspěšném attach — list/detail si refreshne data. */
  onSuccess?: () => void;
}

/**
 * Modal pro nahrání HTML výpisu z nahlížení do KN ke konkrétní (nezatříděné)
 * nemovitosti. Dvě cesty paralelně:
 *   1. drop / file picker — uloženou stránku z prohlížeče (.html)
 *   2. textarea — vložený plain text z výpisu (Ctrl+A / Ctrl+C ze stránky),
 *      použije se když Chrome save-as poškodí UTF-8 (ztrácí byte \x8D u "č/ě/š/ř").
 */
export default function KatastrUploadModal({ propertyId, title, onClose, onSuccess }: Props) {
  const qc = useQueryClient();
  const [dragOver, setDragOver] = useState(false);
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const attach = useMutation({
    mutationFn: async (payload: { file?: File; text?: string }) => {
      let res: Response;
      if (payload.file) {
        const form = new FormData();
        form.append('file', payload.file);
        res = await fetch(`/api/properties/${propertyId}/attach-katastr`, {
          method: 'POST',
          body: form,
        });
      } else {
        res = await fetch(`/api/properties/${propertyId}/attach-katastr`, {
          method: 'POST',
          headers: { 'Content-Type': 'text/html' },
          body: payload.text,
        });
      }
      const data = await res.json();
      if (!res.ok) {
        throw new ApiError(res.status, (data as { message?: string })?.message || `HTTP ${res.status}`, data);
      }
      return data as { ok: true; warnings: string[] };
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['property', propertyId] });
      qc.invalidateQueries({ queryKey: ['properties'] });
      onSuccess?.();
      onClose();
    },
    onError: (e: unknown) => {
      const msg =
        e instanceof ApiError
          ? `${e.message}${typeof e.body === 'object' && e.body && 'message' in e.body ? ' — ' + (e.body as { message?: string }).message : ''}`
          : e instanceof Error
            ? e.message
            : String(e);
      setError(msg);
    },
  });

  const busy = attach.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="bg-background rounded-lg border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto"
      >
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Vložit výpis z katastru</h2>
            {title && <div className="text-xs text-muted-foreground mt-0.5">{title}</div>}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted text-muted-foreground"
            aria-label="Zavřít"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files?.[0];
              if (file) attach.mutate({ file });
            }}
            className={cn(
              'block rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors',
              dragOver ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30',
              busy && 'opacity-50 pointer-events-none',
            )}
          >
            <input
              type="file"
              accept=".html,.htm,text/html"
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) attach.mutate({ file });
                e.target.value = '';
              }}
            />
            <div className="flex items-center justify-center gap-2 text-sm font-medium">
              <UploadIcon size={16} />
              Přetáhni HTML, nebo klikni
            </div>
            <div className="text-xs text-muted-foreground mt-1">Uložená stránka z nahlížení (.html)</div>
          </label>

          <div className="relative flex items-center text-[10px] uppercase tracking-wide text-muted-foreground">
            <div className="flex-1 border-t"></div>
            <span className="px-2">nebo vlož text</span>
            <div className="flex-1 border-t"></div>
          </div>

          <div className="space-y-2">
            <textarea
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="Na stránce výpisu Ctrl+A → Ctrl+C → sem Ctrl+V. Použij, když uložené HTML má rozbitou diakritiku (Chrome save-as občas zničí č/ě/š/ř)."
              className="w-full h-40 rounded-md border p-2 font-mono text-xs"
              disabled={busy}
            />
            <button
              onClick={() => attach.mutate({ text: pasted })}
              disabled={busy || pasted.trim().length < 50}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
            >
              {busy ? 'Parsuju…' : 'Vložit text'}
            </button>
          </div>

          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
